// src/lib/owners/projectOwnerGate.ts
//
// Pure decision logic for the project-record OWNER picker gate. Extracted from
// the inline computation in src/App.tsx (the "Owner" crp-field) so the invariant
// can be unit-guarded under src/lib/** — see docs/REGRESSIONS.md.
//
// THE RULE (unchanged for real projects): an owner may be assigned only once the
// project has BOTH a job-site address AND a start date, the caller has the
// assign_subcontractors capability, and the project is not closed.
//
// THE BUG this fixes: in the "Assign a Project to an Owner" / "Approving a
// Project for Owner" walkthroughs (the Test Project tour), the owner dropdown is
// permanently DISABLED ("— Add address & start date first —"), so the owner step
// can never proceed. The project record edits into LOCAL live-preview state:
//   - start_date  → staged into prjPendingChanges → shows via `effective`
//   - a new address typed/picked in the add-address form → projectNewAddrForm
// …but the ORIGINAL gate read projectDetailData.start_date + projectAddresses
// DIRECTLY, so those live-preview edits updated the DISPLAY yet never unlocked
// the owner. In tutorial mode all writes are no-op'd (WalkthroughPlayer fetch
// shim), so the address/start-date can NEVER persist to projectDetailData /
// projectAddresses — the gate could never open. See reference_walkthrough-tutorial-system.
//
// THE FIX (SAFE — tutorial-scoped): when a walkthrough is active, the gate ALSO
// honours the pending-inclusive `effective` start date and the typed-but-unsaved
// new address, so the live-preview steps unlock the owner. Real (non-tutorial)
// behaviour is BYTE-IDENTICAL to before: with inTutorial=false only the SAVED
// address list / saved scalar address and the SAVED start_date count — a real
// user still cannot assign an owner off unsaved pending edits (that would let an
// owner persist immediately against a start_date that was never saved).

export interface ProjectOwnerGateInputs {
  /** projectAddresses.length — the persisted multi-address list count. */
  addressCount: number
  /** projectDetailData.address — the persisted scalar address (may lag the list). */
  savedAddress?: string | null
  /** projectDetailData.start_date — the PERSISTED start date. */
  savedStartDate?: string | null
  /** effective.start_date — pending-inclusive ({...projectDetailData, ...prjPendingChanges}). */
  effectiveStartDate?: string | null
  /** projectNewAddrForm street/formatted — a typed-but-unsaved new address. */
  pendingNewAddress?: string | null
  /** true while a knowledge-base walkthrough is running (window.__kbWalkthroughActive). */
  inTutorial?: boolean
  /** myCapabilities.assign_subcontractors !== false. */
  capCanAssign: boolean
  /** project is P_COMPLETED / P_CANCELLED / status completed. */
  isLocked: boolean
}

export interface ProjectOwnerGateResult {
  hasAddress: boolean
  hasStartDate: boolean
  canAssign: boolean
}

const nonEmpty = (v: string | null | undefined): boolean => !!(v && String(v).trim())

export function computeProjectOwnerGate(i: ProjectOwnerGateInputs): ProjectOwnerGateResult {
  // Persisted address (the ONLY source that counts for real projects).
  const savedHasAddress = i.addressCount > 0 || nonEmpty(i.savedAddress)
  // Tutorial-only: a typed/picked (but not yet "+ Add address"-saved) address
  // counts, because tutorial writes are no-op'd and can never reach the list.
  const pendingHasAddress = !!i.inTutorial && nonEmpty(i.pendingNewAddress)
  const hasAddress = savedHasAddress || pendingHasAddress

  // Persisted start date (the ONLY source that counts for real projects).
  const savedHasStartDate = nonEmpty(i.savedStartDate)
  // Tutorial-only: a pending (effective) start date counts.
  const pendingHasStartDate = !!i.inTutorial && nonEmpty(i.effectiveStartDate)
  const hasStartDate = savedHasStartDate || pendingHasStartDate

  const canAssign = hasAddress && hasStartDate && i.capCanAssign && !i.isLocked
  return { hasAddress, hasStartDate, canAssign }
}
